import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import api from '../../api'; 
import { getProfilePictureUrl } from '../../utils/apiHelper';
import defaultProfilePic from '../../assets/defaultProfilePic.png'; 
import './EditProfile.css';
import UsersTopbar from '../UsersTopbar/UsersTopbar';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import countryCodes from '../../data/country-codes.json';

const EditProfile = () => {
  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page
  const navigate = useNavigate();
  const { token, profile, logout } = useAuth(); 
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91', // Default country code
    profilePicture: null,
  });

  const [preview, setPreview] = useState(defaultProfilePic); 
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/login'); 
      return;
    }
  
  
    const fetchProfile = async () => {
      try {
        const response = await axios.get(api.getProfile, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        const { name, email, phone, countryCode, profilePicture } = response.data;
  
     
        setFormData({ 
          name, 
          email, 
          phone,
          countryCode: countryCode || '+91', // Default to +91 if not set
          profilePicture: null,
        });
        
       
        const profileImageUrl = getProfilePictureUrl(profilePicture, api.img, defaultProfilePic, name);
  
        setPreview(profileImageUrl); 
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to fetch profile data.');
      }
    };
  
    fetchProfile();
  }, [token, navigate]);
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
      profilePicture: prevFormData.profilePicture, // Ensure profilePicture is not lost
    }));
  };
  

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, profilePicture: file });
      setPreview(URL.createObjectURL(file)); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedData = new FormData();
      updatedData.append('name', formData.name);
      updatedData.append('email', formData.email);
      updatedData.append('phone', formData.phone);
      updatedData.append('countryCode', formData.countryCode); // Include countryCode
  
      if (formData.profilePicture instanceof File) {
        updatedData.append('profilePicture', formData.profilePicture);
      } else if (preview !== defaultProfilePic) {
        updatedData.append('profilePicture', preview.replace(api.img, '')); 
      }
  
      const response = await axios.put(api.updateProfile, updatedData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
  
      setSuccessMessage(response.data.message);
      setError('');
      navigate('/home');
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error?.response?.data?.message || 'An error occurred');
      setSuccessMessage('');
    }
  };
  
  
  


  if (!token) {
    return null; 
  }

  return (
    <div>
      <UsersTopbar />
      <div className="edit-profile-container">
             
        <form onSubmit={handleSubmit}>
          <button 
            className="edit-profile-close-button" 
            onClick={() => navigate('/home')}
            aria-label="Close"
            type="button"
          >
          ×
          </button>
          <h2>Edit Profile</h2>

          {error && <div className="error">{error}</div>}
          {successMessage && <div className="success">{successMessage}</div>}

          <div className="profile-picture-container">
            <label htmlFor="profilePicture" className="custom-file-input">
              <img
                src={preview}
                alt="Profile Preview"
                className="profile-pictures"
              />
              <div className="camera-icon">
                <span className="material-icons">photo_camera</span>
              </div>
            </label>
            <input
              id="profilePicture"
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>

          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Full Name"
            required
          />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
            required
            disabled
          />
          <div className="phone-input-group">
            <select
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              className="country-code-select"
            >
              {countryCodes.map((country) => (
                <option key={country.code + country.name} value={country.code}>
                  {country.code} ({country.name})
                </option>
              ))}
            </select>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number"
              className="phone-input"
              required
            />
          </div>

          <div className="button-group">
         
            <button type="button" className="cancel-button" onClick={() => navigate('/home')}>
            Cancel
            </button>
            <button type="submit" className="save-button">Update Profile</button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditProfile;
